import { INCEPTION_EFFORTS } from '../../shared/types.ts'
import type { HealthResponse, Settings } from '../../shared/types.ts'
import { api } from '../lib/api.ts'
import { Sheet } from './Sheet.tsx'

const INCEPTION_MODELS = [
  { id: 'mercury-2', note: 'newest, recommended' },
  { id: 'mercury', note: 'previous generation' },
  { id: 'mercury-small', note: 'smallest and fastest' },
  { id: 'mercury-coder', note: 'code-tuned' },
  { id: 'mercury-coder-small', note: 'code-tuned, smallest' },
]

// Measured on the real API: sonar and sonar-pro run at about the same speed (~100
// tok/s), so the choice is depth and cost, not latency. sonar-reasoning was retired.
const PERPLEXITY_MODELS = [
  { id: 'sonar', note: 'cheapest — recommended' },
  { id: 'sonar-pro', note: 'deeper search, costs more' },
  { id: 'sonar-reasoning-pro', note: 'thinks first, much slower' },
]

export function SettingsSheet({ settings, onSettings, health, onClose }: {
  settings: Settings
  onSettings: (s: Settings) => void
  health: HealthResponse | null
  onClose: () => void
}) {
  const locked = new Set<keyof Settings>(health?.envLocked ?? [])
  const set = async <K extends keyof Settings>(key: K, value: Settings[K]) => {
    onSettings(await api.saveSettings({ ...settings, [key]: value }))
  }

  /** A value pinned in .env is shown, but not offered as editable. */
  const Pinned = ({ field }: { field: keyof Settings }) =>
    locked.has(field) ? <small className="pinned">fixed by .env</small> : null

  return (
    <Sheet title="Settings" onClose={onClose}>
      <section className="block">
        <h3>Perplexity</h3>
        <p className="muted small">
          Reads the live web and writes the recipes. This is the one doing the cooking, and
          nearly all of the wait — recipes stream in as they are written.
        </p>
        <label className="field">
          <span>Model <Pinned field="perplexityModel" /></span>
          <select
            className="input"
            value={settings.perplexityModel}
            disabled={locked.has('perplexityModel')}
            onChange={(e) => void set('perplexityModel', e.target.value)}
          >
            {PERPLEXITY_MODELS.map((m) => <option key={m.id} value={m.id}>{m.id} — {m.note}</option>)}
            {!PERPLEXITY_MODELS.some((m) => m.id === settings.perplexityModel) && (
              <option value={settings.perplexityModel}>{settings.perplexityModel}</option>
            )}
          </select>
        </label>
        <label className="field">
          <span>Sources from</span>
          <select className="input" value={settings.searchRecency} onChange={(e) => void set('searchRecency', e.target.value as Settings['searchRecency'])}>
            <option value="any">any time</option>
            <option value="year">the past year</option>
            <option value="month">the past month</option>
            <option value="week">the past week</option>
          </select>
        </label>
        <label className="field">
          <span>Recipes per answer</span>
          <input className="input" type="number" min={1} max={5} value={settings.recipeCount}
            onChange={(e) => void set('recipeCount', Math.max(1, Math.min(5, Number(e.target.value) || 3)))} />
          <small>Each recipe is about six seconds of writing. Two instead of three is the quickest win.</small>
        </label>
        <div className={`key-pill ${health?.perplexity ? 'on' : 'off'}`}>
          <span className="dot" />{health?.perplexity ? 'key loaded' : 'PERPLEXITY_API_KEY missing'}
        </div>
      </section>

      <section className="block">
        <h3>Inception</h3>
        <p className="muted small">
          Reads your typed setup answers into structured fields. Mercury is a diffusion model —
          it answers in a few hundred milliseconds, which is what keeps the setup chat feeling
          like a conversation rather than a form.
        </p>
        <label className="field">
          <span>Model <Pinned field="inceptionModel" /></span>
          <select
           className="input"
            value={settings.inceptionModel}
            disabled={locked.has('inceptionModel')}
            onChange={(e) => void set('inceptionModel', e.target.value)}
          >
            {INCEPTION_MODELS.map((m) => <option key={m.id} value={m.id}>{m.id} — {m.note}</option>)}
            {!INCEPTION_MODELS.some((m) => m.id === settings.inceptionModel) && (
              <option value={settings.inceptionModel}>{settings.inceptionModel}</option>
            )}
          </select>
        </label>
        <label className="field">
          <span>Reasoning effort <Pinned field="inceptionEffort" /></span>
          <select
           className="input"
            value={settings.inceptionEffort}
            disabled={locked.has('inceptionEffort')}
            onChange={(e) => void set('inceptionEffort', e.target.value as Settings['inceptionEffort'])}
          >
            {INCEPTION_EFFORTS.map((e) => <option key={e} value={e}>{e}</option>)}
          </select>
          <small>Setup answers are a parsing job, not a thinking one — <code>instant</code> is usually right.</small>
        </label>
        <div className={`key-pill ${health?.inception ? 'on' : 'off'}`}>
          <span className="dot" />{health?.inception ? 'key loaded' : 'INCEPTION_API_KEY missing'}
        </div>
      </section>

      <section className="block">
        <h3>Data</h3>
        <p className="muted small">
          Your setup, conversation and saved recipes live in <code>data/store.json</code> in the project
          folder. Nothing leaves the machine except the provider calls. Keys stay in <code>.env</code> on
          the server and never reach the browser.
        </p>
      </section>
    </Sheet>
  )
}
